import { ConfirmService } from '../../../core/services/confirm.service';
import { Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { IconComponent } from '../../icon/icon.component';
import { AttachmentService } from '../../../core/services/attachment.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_MAX_BYTES,
  Attachment,
  AttachmentOwner,
} from '../../../core/models/attachment.model';

type FileKind = 'image' | 'pdf' | 'excel' | 'word' | 'other';

const KIND_STYLE: Record<FileKind, { label: string; classes: string }> = {
  image: { label: 'IMG', classes: 'bg-action-light text-action' },
  pdf: { label: 'PDF', classes: 'bg-status-danger/10 text-status-danger' },
  excel: { label: 'XLS', classes: 'bg-brand-light text-brand' },
  word: { label: 'DOC', classes: 'bg-status-info/10 text-status-info' },
  other: { label: 'FILE', classes: 'bg-surface-canvas text-ink-secondary' },
};

/**
 * "Attachments" card for customer, vendor, PO, SO and expense pages: drag & drop (or browse) upload of
 * images, PDF, Excel and Word files, download, "valid until" date and delete.
 */
@Component({
  selector: 'pp-attachments',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './attachments.component.html',
})
export class AttachmentsComponent implements OnChanges {
  private readonly confirmDialog = inject(ConfirmService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly toast = inject(ToastService);

  @Input({ required: true }) owner!: AttachmentOwner;
  @Input({ required: true }) ownerId!: string;
  /** Without the card border/shadow, for use inside drawers and modals. */
  @Input() embedded = false;
  /** False for users who can only see the record: hides upload, delete and "valid until" editing. */
  @Input() canEdit = true;

  readonly accept = ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`).join(',');
  readonly items = signal<Attachment[]>([]);
  readonly loading = signal(true);
  readonly uploading = signal(false);
  readonly dragOver = signal(false);
  readonly errors = signal<string[]>([]);
  readonly editingValidUntilId = signal<string | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ownerId'] && this.ownerId) this.load();
  }

  load(): void {
    this.loading.set(true);
    this.attachmentService.list(this.owner, this.ownerId).subscribe({
      next: (items) => {
        this.items.set(items);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  // ---- Drag & drop ----
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    this.dragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    this.upload(Array.from(event.dataTransfer?.files ?? []));
  }

  onBrowse(input: HTMLInputElement): void {
    this.upload(Array.from(input.files ?? []));
    input.value = '';
  }

  upload(files: File[]): void {
    if (!files.length || this.uploading()) return;

    const errors: string[] = [];
    const valid = files.filter((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ATTACHMENT_EXTENSIONS.includes(ext)) {
        errors.push(`${file.name}: only images, PDF, Excel and Word files are allowed.`);
        return false;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        errors.push(`${file.name}: the file is larger than 10 MB.`);
        return false;
      }
      return true;
    });
    this.errors.set(errors);
    if (!valid.length) return;

    this.uploading.set(true);
    this.attachmentService.upload(this.owner, this.ownerId, valid).subscribe({
      next: (uploaded) => {
        this.uploading.set(false);
        this.items.update((items) => [...uploaded, ...items]);
        this.toast.success(uploaded.length === 1 ? 'File uploaded' : `${uploaded.length} files uploaded`);
      },
      error: (error: HttpErrorResponse) => {
        this.uploading.set(false);
        const serverErrors = Object.values((error.error?.errors ?? {}) as Record<string, string[]>).flat();
        this.errors.set([
          ...errors,
          ...(serverErrors.length
            ? serverErrors
            : [
                error.status === 413
                  ? 'The upload is too large for the server.'
                  : error.status === 0
                    ? 'Cannot reach the server. Check your connection and try again.'
                    : (error.error?.message ?? 'The files could not be uploaded. Please try again.'),
              ]),
        ]);
      },
    });
  }

  download(item: Attachment): void {
    this.attachmentService.download(item);
  }

  async remove(item: Attachment): Promise<void> {
    if (!(await this.confirmDialog.ask({ title: `Delete ${item.name}?`, message: 'The file will be removed from this record.' }))) return;
    this.attachmentService.remove(item.id).subscribe(() => {
      this.items.update((items) => items.filter((i) => i.id !== item.id));
    });
  }

  saveValidUntil(item: Attachment, value: string): void {
    this.editingValidUntilId.set(null);
    const validUntil = value || null;
    if ((item.validUntil ?? null) === validUntil) return;
    this.attachmentService.setValidUntil(item.id, validUntil).subscribe((updated) => {
      this.items.update((items) => items.map((i) => (i.id === updated.id ? updated : i)));
    });
  }

  isExpired(item: Attachment): boolean {
    return !!item.validUntil && item.validUntil < new Date().toISOString().slice(0, 10);
  }

  kind(item: Attachment): { label: string; classes: string } {
    return KIND_STYLE[kindOf(item.name)];
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

function kindOf(name: string): FileKind {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['xls', 'xlsx'].includes(ext)) return 'excel';
  if (['doc', 'docx'].includes(ext)) return 'word';
  return 'other';
}
